// Pantalla de creación/edición de preset reutilizando ClientRuleFormSheet (mismo editor que /clients/:id/rules).
import {
  configurationPageClass,
  configurationPageContentClass,
} from '@modules/configuration/components/configurationLayout';
import { Input } from '@components/ui/shadcn/input';
import { faFileLines, faGear, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClientRuleBoardCard } from '@modules/Clients/ClientRules/components/ClientRuleBoardCard';
import { ClientRuleFormSheet } from '@modules/Clients/ClientRules/components/ClientRuleFormSheet';
import { CardList } from '@modules/configuration/Rules/components/display';
import { RulesModel } from '@modules/configuration/Rules/models/Rules';
import { prefetchRulesReferenceData } from '@modules/configuration/Rules/services/prefetchRulesReferenceData';
import { ShippingRulePresetsApiAdapter } from '@modules/configuration/ShippingRulePresets/adapters/ShippingRulePresetsApiAdapter';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal, ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';
import { TextArea } from '@shared/components/ui/textarea';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { usePresetActions } from '../../hooks/usePresetActions';
import {
  normalizePresetRulesForApi,
  presetDraftToRulesModel,
  shippingRuleFormPayloadToPresetRule,
  stripTempId,
  PresetRuleDraft,
} from '../../utils/presetRuleDraft';
import { ErrorState } from '../feedback/ErrorState';
import { LoadingState } from '../feedback/LoadingState';
import { PresetRuleEditorBreadcrumb } from './PresetRuleEditorBreadcrumb';

type FormMode = 'create' | 'edit';

export const PresetRuleEditorContainer = () => {
  const { t } = useTranslation('shippingRulePresetsModule');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { presetId: presetIdParam } = useParams<{ presetId?: string }>();

  const mode: 'create' | 'edit' = presetIdParam ? 'edit' : 'create';
  const presetNumericId = presetIdParam ? Number(presetIdParam) : NaN;

  const [presetName, setPresetName] = useState('');
  const [description, setDescription] = useState('');
  const [draftRules, setDraftRules] = useState<PresetRuleDraft[]>([]);

  const nextTempIdRef = useRef(-1);
  const lastHydratedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    lastHydratedKeyRef.current = null;
  }, [presetNumericId]);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [formSession, setFormSession] = useState(0);

  const confirmDeleteRuleRef = useRef<ConfirmModalRef>(null);
  const deleteTempIdRef = useRef<number | null>(null);

  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const presetsAdapter = useMemo(() => new ShippingRulePresetsApiAdapter(httpClient), [httpClient]);

  const {
    createPreset,
    updatePreset,
    isLoading: savingPreset,
  } = usePresetActions(() => {
    navigate('/configuration/shipping-rule-presets');
  });

  const {
    data: loadedPreset,
    isLoading: loadingPreset,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ['shipping-rule-preset-one', presetNumericId],
    queryFn: () => presetsAdapter.getPresetById(presetNumericId),
    enabled: mode === 'edit' && Number.isFinite(presetNumericId),
    staleTime: 0,
  });

  useEffect(() => {
    void prefetchRulesReferenceData();
  }, []);

  useEffect(() => {
    if (mode !== 'edit' || !loadedPreset) return;
    const hydrationKey = `${presetNumericId}:${loadedPreset.updated_at}`;
    if (lastHydratedKeyRef.current === hydrationKey) return;
    lastHydratedKeyRef.current = hydrationKey;

    setPresetName(loadedPreset.name);
    setDescription(loadedPreset.description ?? '');
    let tid = -1;
    const drafts: PresetRuleDraft[] = loadedPreset.rules_payload.map((r) => ({
      ...r,
      tempId: tid--,
    }));
    setDraftRules(drafts);
    nextTempIdRef.current = -(loadedPreset.rules_payload.length + 1);
  }, [mode, loadedPreset, presetNumericId]);

  const initialRuleForEdit = useMemo(() => {
    if (formMode !== 'edit' || editingRuleId == null) return undefined;
    const draft = draftRules.find((r) => r.tempId === editingRuleId);
    if (!draft) return undefined;
    const idx = draftRules.findIndex((r) => r.tempId === editingRuleId);
    return presetDraftToRulesModel(draft, Math.max(0, idx));
  }, [formMode, editingRuleId, draftRules]);

  const openCreate = useCallback(() => {
    setFormSession((s) => s + 1);
    setFormMode('create');
    setEditingRuleId(null);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((tempId: number) => {
    setFormSession((s) => s + 1);
    setFormMode('edit');
    setEditingRuleId(tempId);
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setEditingRuleId(null);
  }, []);

  const handleLocalPersist = useCallback(
    async (payload: Record<string, unknown>, ctx: { isEditMode: boolean; activeRuleId?: number }) => {
      const sanitizedPayload = { ...(payload as Record<string, unknown> & { team_id?: number }) };
      delete sanitizedPayload.team_id;
      const presetRule = shippingRuleFormPayloadToPresetRule(sanitizedPayload);
      if (!ctx.isEditMode) {
        const tid = nextTempIdRef.current--;
        setDraftRules((prev) => [...prev, { ...presetRule, tempId: tid }]);
      } else if (ctx.activeRuleId != null) {
        setDraftRules((prev) =>
          prev.map((r) => (r.tempId === ctx.activeRuleId ? { ...presetRule, tempId: r.tempId } : r)),
        );
      }
    },
    [],
  );

  const handleToggleRule = useCallback((tempId: number) => {
    setDraftRules((prev) => prev.map((r) => (r.tempId === tempId ? { ...r, is_active: !(r.is_active !== false) } : r)));
  }, []);

  const rulesForList = useMemo(
    () => draftRules.map((draft, index) => presetDraftToRulesModel(draft, index)),
    [draftRules],
  );

  const handleReorder = useCallback((newOrder: RulesModel[]) => {
    setDraftRules((prev) => {
      const orderByTempId = new Map(newOrder.map((r, index) => [r.id, index]));
      return [...prev].sort((a, b) => (orderByTempId.get(a.tempId) ?? 0) - (orderByTempId.get(b.tempId) ?? 0));
    });
  }, []);

  const runDeleteRule = useCallback((tempId: number) => {
    setDraftRules((prev) => prev.filter((r) => r.tempId !== tempId));
  }, []);

  const confirmDeleteRule = useCallback(
    (tempId: number) => {
      deleteTempIdRef.current = tempId;
      confirmDeleteRuleRef.current?.open(
        () => {
          if (deleteTempIdRef.current != null) runDeleteRule(deleteTempIdRef.current);
        },
        undefined,
        t('editor.delete-rule-title'),
        t('editor.delete-rule-message'),
      );
    },
    [runDeleteRule, t],
  );

  const handleSavePreset = useCallback(async () => {
    if (!presetName.trim()) {
      toast({
        title: t('createEditModal.validation.nameRequired'),
        variant: 'destructive',
      });
      return;
    }
    if (draftRules.length === 0) {
      toast({
        title: t('editor.no-rules-title'),
        description: t('editor.no-rules-description'),
        variant: 'destructive',
      });
      return;
    }
    const rulesWithoutTempId = draftRules.map(stripTempId);
    const { rules, error } = normalizePresetRulesForApi(rulesWithoutTempId);
    if (error) {
      toast({
        title: t('toasts.create.error.title'),
        description: error,
        variant: 'destructive',
      });
      return;
    }

    if (mode === 'edit' && Number.isFinite(presetNumericId)) {
      await updatePreset(presetNumericId, {
        name: presetName.trim(),
        description: description.trim() || null,
        rules,
      });
    } else {
      await createPreset({
        name: presetName.trim(),
        description: description.trim() || null,
        rules,
      });
    }
  }, [presetName, description, draftRules, mode, presetNumericId, createPreset, updatePreset, t, toast]);

  const headerActions = useMemo(
    () => [
      {
        id: 'cancel-preset-editor',
        label: t('buttons.cancel'),
        onClick: () => navigate('/configuration/shipping-rule-presets'),
        variant: 'outline' as const,
      },
      {
        id: 'save-preset-editor',
        label: t('editor.save-preset'),
        onClick: () => void handleSavePreset(),
        variant: 'primary' as const,
        disabled: savingPreset,
      },
    ],
    [t, navigate, handleSavePreset, savingPreset],
  );

  if (mode === 'edit' && loadError) {
    return (
      <div className={configurationPageClass}>
        <ErrorState error={loadError as Error} onRetry={() => refetch()} />
      </div>
    );
  }

  if (mode === 'edit' && loadingPreset && !loadedPreset) {
    return <LoadingState />;
  }

  return (
    <div className={`${configurationPageClass} pb-6`}>
      <PresetRuleEditorBreadcrumb mode={mode} presetName={presetName} headerActions={headerActions} />

      <div className={`${configurationPageContentClass} flex flex-col gap-4`}>
        {/* Información Básica */}
        <section className="rounded-xl border border-card-border bg-card-bg p-5 pb-6">
          <div className="mb-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-title">
              <FontAwesomeIcon icon={faFileLines} className="w-3.5 h-3.5 text-icons" />
              {t('editor.basic.title')}
            </h3>
            <p className="text-sm text-subtext">{t('editor.basic.description')}</p>
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-title">
                {t('createEditModal.fields.name')}
                <span className="text-error ml-1">*</span>
              </label>
              <Input
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder={t('createEditModal.fields.namePlaceholder')}
                data-testid="preset-editor-name-input"
              />
              <p className="mt-1 text-xs text-subtext">{t('editor.basic.name-help')}</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-title">
                {t('createEditModal.fields.description')}
              </label>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('createEditModal.fields.descriptionPlaceholder')}
                rows={2}
                className="resize-none"
                data-testid="preset-editor-description-input"
              />
              <p className="mt-1 text-xs text-subtext">{t('editor.basic.description-help')}</p>
            </div>
          </div>
        </section>

        {/* Configuración de Reglas */}
        <section className="rounded-xl border border-card-border bg-card-bg p-5">
          <div className="mb-4">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-title">
              <FontAwesomeIcon icon={faGear} className="w-3.5 h-3.5 text-icons" />
              {t('editor.rules.title')}
            </h3>
            <p className="text-sm text-subtext">{t('editor.rules.description')}</p>
          </div>

          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h4 className="text-base font-semibold text-title">{t('editor.rules.list-title')}</h4>
              <p className="text-sm text-subtext">{t('editor.rules.list-description')}</p>
            </div>
            <Button type="button" size="sm" onClick={openCreate} data-testid="preset-editor-add-rule-button">
              <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
              {t('editor.add-rule')}
            </Button>
          </div>

          {/* Listado de reglas (misma tarjeta y reordenación que reglas de cliente) */}
          <div>
            {draftRules.length === 0 ? (
              <div className="rounded-lg border border-card-border bg-body-bg px-4 py-3 text-sm text-subtext">
                {t('editor.rules.empty-message')}
              </div>
            ) : (
              <CardList
                rules={rulesForList}
                onEditRule={(rule) => openEdit(rule.id)}
                onDeleteRule={(rule) => confirmDeleteRule(rule.id)}
                onToggleRule={(rule) => handleToggleRule(rule.id)}
                onReorder={handleReorder}
                CardComponent={ClientRuleBoardCard}
                canEdit
                canDelete
                isReadOnly={false}
                reorderMode="local"
                autoHeight
                estimateItemHeight={360}
                className="border-0 bg-transparent shadow-none"
              />
            )}
          </div>
        </section>
      </div>

      {formOpen && (
        <ClientRuleFormSheet
          key={formSession}
          onClose={closeForm}
          mode={formMode}
          ruleId={editingRuleId}
          initialRule={initialRuleForEdit}
          localPersist={handleLocalPersist}
        />
      )}

      <ConfirmModal ref={confirmDeleteRuleRef} />
    </div>
  );
};
